import React from 'react';
import { Box } from '@mui/material';
import DosenButton from '../../../../../components/ui/Buttons/DosenButton';
import DosenTooltip from '../../../../../components/ui/DosenTooltip';

interface StepFooterProps {
  isFirstStep: boolean;
  isLastStep: boolean;
  isSaving: boolean;
  /** Next button label while `isSaving` (e.g. "Publishing..."). */
  savingLabel?: string;
  onCancel: () => void;
  onPrev: () => void;
  onNext: () => void;
  nextLabel?: string;
  /** Override the entire left side of the footer (replaces Previous/Cancel). */
  leftContent?: React.ReactNode;
  /** Inject extra content between the left controls and the Next button. */
  rightExtraContent?: React.ReactNode;
  /** Disable the next button (grayed out, no click). */
  nextDisabled?: boolean;
  /** Save button shown to the left of the main CTA (content step only). */
  saveAction?: { label: string; onClick: () => void; active: boolean };
  /** Tooltip shown on Save when it's disabled (e.g. video generating). */
  saveDisabledTooltip?: string;
  /** Tooltip shown on the Next/Create button when it's disabled. */
  nextDisabledTooltip?: string;
  /** Tooltip shown on the Next button while ENABLED (e.g. the Publish note). */
  nextTooltip?: string;
}

const StepFooter: React.FC<StepFooterProps> = ({
  isFirstStep,
  isLastStep,
  isSaving,
  savingLabel = 'Saving...',
  onCancel,
  onPrev,
  onNext,
  nextLabel,
  leftContent,
  rightExtraContent,
  nextDisabled,
  saveAction,
  saveDisabledTooltip,
  nextDisabledTooltip,
  nextTooltip
}) => {
  const label = nextLabel || (isLastStep ? 'Done' : 'Next');

  return (
    <Box
      sx={{
        width: '100%',
        height: 96,
        px: 3,
        borderTop: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}
    >
      {/* Left side */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '10px' }}>
        {leftContent || (isFirstStep ? (
          <DosenButton tone="basic" sizeVariant="lg" onClick={onCancel}>
            Cancel
          </DosenButton>
        ) : (
          <DosenButton tone="basic" sizeVariant="lg" onClick={onPrev}>
            Previous
          </DosenButton>
        ))}
      </Box>

      {/* Right side */}
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px' }}>
        {rightExtraContent}
        {saveAction && (
          <DosenTooltip title={!saveAction.active && saveDisabledTooltip ? saveDisabledTooltip : ''}>
            <span>
              <DosenButton
                tone={saveAction.active ? 'cta' : 'basic'}
                sizeVariant="lg"
                disabled={!saveAction.active}
                onClick={saveAction.active ? saveAction.onClick : undefined}
              >
                {saveAction.label}
              </DosenButton>
            </span>
          </DosenTooltip>
        )}
        <DosenTooltip title={nextDisabled && nextDisabledTooltip ? nextDisabledTooltip : nextTooltip || ''}>
          <span>
            <DosenButton
              tone="cta"
              sizeVariant="lg"
              loading={isSaving}
              loadingText={savingLabel}
              disabled={nextDisabled}
              onClick={onNext}
            >
              {label}
            </DosenButton>
          </span>
        </DosenTooltip>
      </Box>
    </Box>
  );
};

export default StepFooter;
